import {
  approveDecision,
  createOrUpdateTemplate,
  createOrUpdateTool,
  deleteTemplate,
  deleteTool,
  executeStep,
  getActiveRun,
  getAgentWorkflowRun,
  getAgentWorkflowRunDebugger,
  getDebugStepOutputs,
  getDecisionStats,
  getExecutionStatus,
  getMlHealth,
  getMlRegime,
  getMlStrategyScores,
  getModel,
  getModelRuntimePolicy,
  getRiskStatus,
  getRunHealthAggregates,
  getSkill,
  getTemplate,
  getTool,
  getToolGaps,
  listActiveAgents,
  listAgentWorkflowRuns,
  listAgentWorkflows,
  listEconomicEvents,
  listLlmCallCostOutliers,
  listLlmCallLatencyOutliers,
  listModelRuntimePolicies,
  listModels,
  listPendingDecisions,
  listPromptTemplates,
  listRuns,
  listSkills,
  listTemplates,
  listTools,
  pauseExecution,
  previewPrompt,
  recommendAgentTemplates,
  rejectDecision,
  resolveModelRuntimePolicy,
  resumeExecution,
  runAgentWorkflowRunAction,
  saveDebugStepOutput,
  searchAgentTemplates,
  setTemplateStatus,
  setToolStatus,
  spawnAgent,
  stopExecution,
  terminateAgent,
  triggerAgentWorkflow,
  updateModelRuntimePolicy,
  createModelRuntimePolicy,
} from '../../generated/api/sdk.gen.js';
import type {
  AgentExecuteRequest,
  AgentTemplateStatusUpdate,
  AgentTemplateUpsert,
  AgentTerminateRequest,
  CreateModelRuntimePolicyData,
  ExecuteStepData,
  GetAgentWorkflowRunDebuggerData,
  GetRunHealthAggregatesData,
  ListLlmCallCostOutliersData,
  ListLlmCallLatencyOutliersData,
  ListSkillsData,
  ModelRuntimePolicyResolveRequest,
  PreviewPromptData,
  RunAgentWorkflowRunActionData,
  SaveDebugStepOutputData,
  ToolStatusUpdate,
  ToolUpsert,
  UpdateModelRuntimePolicyData,
  WorkflowTriggerRequest,
} from '../../generated/api/types.gen.js';
import { unwrapGenerated } from './generated.js';

export const agentApi = {
  listTemplates: (includeDisabled = false) => unwrapGenerated(listTemplates({ query: { includeDisabled } })),
  getTemplate: (slug: string) => unwrapGenerated(getTemplate({ path: { templateSlug: slug } })),
  updateTemplate: (slug: string, data: AgentTemplateUpsert) =>
    unwrapGenerated(createOrUpdateTemplate({ path: { templateSlug: slug }, body: data })),
  setTemplateStatus: (slug: string, status: AgentTemplateStatusUpdate['status'], reason?: string) =>
    unwrapGenerated(setTemplateStatus({ path: { templateSlug: slug }, body: { status, reason } })),
  deleteTemplate: (slug: string, force = false) =>
    unwrapGenerated(deleteTemplate({ path: { templateSlug: slug }, query: force ? { force: 'true' } : undefined })),

  listAgents: () => unwrapGenerated(listActiveAgents()),
  spawnAgent: (templateSlug: string) => unwrapGenerated(spawnAgent({ body: { templateSlug } })),
  terminateAgent: (id: string, reason?: AgentTerminateRequest['reason']) =>
    unwrapGenerated(terminateAgent({ path: { agentId: id }, body: { reason } })),

  agentHealth: () => unwrapGenerated(getExecutionStatus()),
  listWorkflows: () => unwrapGenerated(listAgentWorkflows()),
  triggerWorkflow: (id: string, context?: WorkflowTriggerRequest['context']) =>
    unwrapGenerated(triggerAgentWorkflow({ path: { workflowId: id }, body: { context } })),
  listRuns: () => unwrapGenerated(listAgentWorkflowRuns()),
  getRunStatus: (runId: string) => unwrapGenerated(getAgentWorkflowRun({ path: { runId } })),
  getRunDebugger: (
    runId: string,
    detail: NonNullable<GetAgentWorkflowRunDebuggerData['query']>['detail'] = 'summary',
  ) => unwrapGenerated(getAgentWorkflowRunDebugger({ path: { runId }, query: { detail } })) as Promise<unknown>,
  runWorkflowAction: (runId: string, data: NonNullable<RunAgentWorkflowRunActionData['body']>) =>
    unwrapGenerated(runAgentWorkflowRunAction({ path: { runId }, body: data })),

  pendingDecisions: () => unwrapGenerated(listPendingDecisions()),
  approveDecision: (id: string, notes?: string) =>
    unwrapGenerated(approveDecision({ path: { requestId: id }, body: { notes } })),
  rejectDecision: (id: string, notes?: string) =>
    unwrapGenerated(rejectDecision({ path: { requestId: id }, body: { notes } })),
  decisionStats: () => unwrapGenerated(getDecisionStats()),

  listTools: (includeDeprecated = false) =>
    unwrapGenerated(listTools({ query: includeDeprecated ? { includeDeprecated: 'true' } : undefined })),
  getTool: (name: string) => unwrapGenerated(getTool({ path: { name } })),
  listSkills: (params?: NonNullable<ListSkillsData['query']>) => unwrapGenerated(listSkills({ query: params })),
  getSkill: (slug: string) => unwrapGenerated(getSkill({ path: { slug } })),
  listModels: () => unwrapGenerated(listModels()),
  getModel: (slug: string) => unwrapGenerated(getModel({ path: { slug } })),
  listRuntimePolicies: () => unwrapGenerated(listModelRuntimePolicies()),
  getRuntimePolicy: (id: number) => unwrapGenerated(getModelRuntimePolicy({ path: { id } })),
  resolveRuntimePolicy: (data: ModelRuntimePolicyResolveRequest) =>
    unwrapGenerated(resolveModelRuntimePolicy({ body: data })),
  createRuntimePolicy: (data: NonNullable<CreateModelRuntimePolicyData['body']>) =>
    unwrapGenerated(createModelRuntimePolicy({ body: data })),
  updateRuntimePolicy: (id: number, data: NonNullable<UpdateModelRuntimePolicyData['body']>) =>
    unwrapGenerated(updateModelRuntimePolicy({ path: { id }, body: data })),
  updateTool: (name: string, data: ToolUpsert) => unwrapGenerated(createOrUpdateTool({ path: { name }, body: data })),
  setToolStatus: (name: string, status: ToolStatusUpdate['status']) =>
    unwrapGenerated(setToolStatus({ path: { name }, body: { status } })),
  deleteTool: (name: string, force = false) =>
    unwrapGenerated(deleteTool({ path: { name }, query: force ? { force: 'true' } : undefined })),

  previewPrompt: (data: NonNullable<PreviewPromptData['body']>) => unwrapGenerated(previewPrompt({ body: data })),
  executeStep: (data: NonNullable<ExecuteStepData['body']>) => unwrapGenerated(executeStep({ body: data })),
  executeStepStream: (_data: AgentExecuteRequest): EventSource | null => null,
  stopExecution: () => unwrapGenerated(stopExecution()),
  pauseExecution: () => unwrapGenerated(pauseExecution()),
  resumeExecution: () => unwrapGenerated(resumeExecution()),
  executionStatus: () => unwrapGenerated(getExecutionStatus()),
  activeRun: () => unwrapGenerated(getActiveRun()),
  runList: () => unwrapGenerated(listRuns()),
  saveDebugStepOutput: (data: NonNullable<SaveDebugStepOutputData['body']>) =>
    unwrapGenerated(saveDebugStepOutput({ body: data })),
  getDebugStepOutputs: (templateId: string) => unwrapGenerated(getDebugStepOutputs({ path: { templateId } })),
  toolGaps: () => unwrapGenerated(getToolGaps()),
  promptTemplates: () => unwrapGenerated(listPromptTemplates()),

  searchTemplates: (query: string, limit = 10) => unwrapGenerated(searchAgentTemplates({ query: { q: query, limit } })),
  recommendTemplates: (stepDescription: string, category?: string, limit = 5) =>
    unwrapGenerated(recommendAgentTemplates({ query: { stepDescription, workflowCategory: category, limit } })),

  // ─── Fleet monitoring (OBS-6) ────────────────────────────────────────────────
  runHealthAggregates: (params?: NonNullable<GetRunHealthAggregatesData['query']>) =>
    unwrapGenerated(getRunHealthAggregates({ query: params })),
  llmCallCostOutliers: (params?: NonNullable<ListLlmCallCostOutliersData['query']>) =>
    unwrapGenerated(listLlmCallCostOutliers({ query: params })),
  llmCallLatencyOutliers: (params?: NonNullable<ListLlmCallLatencyOutliersData['query']>) =>
    unwrapGenerated(listLlmCallLatencyOutliers({ query: params })),

  economicCalendar: () => unwrapGenerated(listEconomicEvents()),
  mlHealth: () => unwrapGenerated(getMlHealth()),
  mlRegime: () => unwrapGenerated(getMlRegime()),
  mlStrategyScores: () => unwrapGenerated(getMlStrategyScores()),
  riskStatus: () => unwrapGenerated(getRiskStatus()),
};
